import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PageContainer } from '@/components/layout/PageContainer';
import { useAuth } from '@/contexts/AuthContext';
import { Toast } from '@/components/common/Toast';
import { SEO } from '@/components/seo/SEO';
import { CANONICAL_URLS } from '@/utils/canonicalUrl';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, user, isLoading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState<{ show: boolean; message: string }>({ show: false, message: '' });

  useEffect(() => {
    if (location.state?.message) {
      setToast({ show: true, message: location.state.message });
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    // 인증 로딩이 완료된 후에만 리다이렉트 처리
    if (!authLoading && user) {
      console.log('[Login] 사용자 역할 기반 리다이렉트:', user.role);
      
      // 사용자 역할에 따라 다른 홈페이지로 리다이렉트
      let defaultPath = '/';
      if (user.role === 'seller') {
        defaultPath = '/seller/home';
        console.log('[Login] 판매자 홈으로 리다이렉트:', defaultPath);
      } else {
        // buyer의 경우 메인 홈 (관리자는 별도 페이지에서 로그인)
        defaultPath = '/';
        console.log('[Login] 구매자 홈으로 리다이렉트:', defaultPath);
      }
      
      const from = location.state?.from || defaultPath;
      navigate(from, { replace: true });
    }
  }, [user, authLoading, navigate, location.state]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleLogin = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    if (!email || !password) {
      setToast({ show: true, message: '이메일과 비밀번호를 입력해주세요' });
      return;
    }

    setIsLoading(true);
    
    // 타임아웃 설정 (30초)
    const timeoutId = setTimeout(() => {
      setIsLoading(false);
      setToast({ show: true, message: '로그인 시간이 초과되었습니다. 다시 시도해주세요.' });
    }, 30000);

    try {
      const { success, error } = await signIn(email, password);
      
      clearTimeout(timeoutId);
      
      if (!success) {
        setToast({ show: true, message: error || '로그인에 실패했습니다' });
        setIsLoading(false);
      } else {
        // 로그인 성공 시 역할별 안내 메시지는 useEffect에서 리다이렉트와 함께 처리
        console.log('[Login] 로그인 성공, 역할별 리다이렉트 대기 중...');
        // 2초 후 로딩 상태 해제 (리다이렉트가 늦어지는 경우를 위한 안전장치)
        setTimeout(() => {
          if (isLoading) {
            console.log('[Login] 리다이렉트 지연으로 인한 로딩 상태 해제');
            setIsLoading(false);
          }
        }, 2000);
      }
    } catch (error) {
      clearTimeout(timeoutId);
      setToast({ show: true, message: '로그인 중 오류가 발생했습니다' });
      setIsLoading(false);
    }
  };

  const handleSignUp = () => {
    navigate('/signup-select');
  };

  const handleFindAccount = () => {
    navigate('/find-account');
  };

  // 인증 로딩 중이면 로딩 스피너 표시
  if (authLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-gray-200 border-t-[#86DB89] rounded-full animate-spin"></div>
          <p className="text-[#666666] text-base">인증 정보 확인 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="로그인 - 에코딜"
        description="에코딜에 로그인하고 최소 40% 할인된 마감 상품을 만나보세요."
        canonical={CANONICAL_URLS.LOGIN}
      />
      <PageContainer>
        {/* 헤더 */}
        <div className="mb-12">
          <div className="flex items-center justify-center relative mb-6">
            <button
              onClick={handleBack}
              className="absolute left-0 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <img
                src="/icons/icon_back.svg"
                alt="뒤로가기"
                className="w-6 h-6"
              />
            </button>
            <h1 className="text-2xl font-bold text-[#222222]">에코딜 시작하기</h1>
          </div>
          <p className="text-xl font-semibold text-[#222222] leading-relaxed text-center">
            로그인이 필요한 서비스입니다.<br />
            로그인해주세요.
          </p>
        </div>

        {/* 로그인 폼 */}
        <form onSubmit={handleLogin} name="login-form" className="max-w-[300px] mx-auto space-y-6">
          {/* 카카오 로그인 버튼 (추후 구현) */}
          <button
            type="button"
            disabled
            className="w-full h-[45px] relative opacity-50 cursor-not-allowed"
          >
            <img
              src="/icons/kakao_login.png"
              alt="카카오 로그인"
              className="w-full h-full object-contain"
            />
          </button>

          {/* 이메일 입력 */}
          <div>
            <label className="block text-base font-medium text-[#222222] mb-1.5">
              이메일
            </label>
            <input
              type="email"
              name="email"
              id="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="이메일 입력"
              className="w-full h-[36px] px-4 text-base border-[1.5px] border-[#979797] rounded-[10px] focus:outline-none focus:border-[#86DB89] placeholder:text-[#979797]"
            />
          </div>

          {/* 비밀번호 입력 */}
          <div>
            <label className="block text-base font-medium text-[#222222] mb-1.5">
              비밀번호
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                id="current-password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="비밀번호 입력"
                className="w-full h-[36px] px-4 pr-10 text-base border-[1.5px] border-[#979797] rounded-[10px] focus:outline-none focus:border-[#86DB89] placeholder:text-[#979797]"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2"
              >
                <img
                  src="/icons/icon_eye.svg"
                  alt={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
                  className="w-5 h-5 opacity-60"
                />
              </button>
            </div>
          </div>

          {/* 이메일 로그인 버튼 */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full h-[45px] bg-[#CCEFCC] text-[#222222] font-semibold text-xl rounded-xl hover:bg-[#B5E5B5] transition-colors disabled:opacity-50"
          >
            {isLoading ? '로그인 중...' : '이메일 로그인'}
          </button>

          {/* 하단 링크 */}
          <div className="flex justify-center items-center gap-8 pt-6">
            <button
              onClick={handleSignUp}
              className="text-base font-medium text-[#222222] border-b-[1.5px] border-[#222222] pb-0.5 hover:opacity-70 transition-opacity"
            >
              회원가입
            </button>
            <button
              onClick={handleFindAccount}
              className="text-base font-medium text-[#222222] border-b-[1.5px] border-[#222222] pb-0.5 hover:opacity-70 transition-opacity"
            >
              아이디/비번찾기
            </button>
          </div>
        </form>
      </PageContainer>

      {/* 토스트 메시지 */}
      {toast.show && (
        <Toast
          message={toast.message}
          isVisible={toast.show}
          onClose={() => setToast({ show: false, message: '' })}
        />
      )}
    </div>
  );
}
